import * as React from 'react';
import { Card as FluentCard, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import type { ComponentProps } from 'react';

type DivProps = React.HTMLAttributes<HTMLDivElement>;
type H3Props = React.HTMLAttributes<HTMLHeadingElement>;
type PProps = React.HTMLAttributes<HTMLParagraphElement>;

export type CardProps = ComponentProps<typeof FluentCard>;

const useCardStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
});

const useSectionStyles = makeStyles({
  header: {
  ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
  ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
  },
  content: {
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  title: {
    margin: 0,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  description: {
    margin: 0,
    color: tokens.colorNeutralForeground3,
  },
});

export const Card: React.FC<CardProps> = ({ className, children, ...rest }) => {
  const styles = useCardStyles();
  return (
    <FluentCard className={mergeClasses(styles.root, className)} {...rest}>
      {children}
    </FluentCard>
  );
};

export const CardHeader: React.FC<DivProps> = ({ className, children, ...rest }) => {
  const styles = useSectionStyles();
  return (
    <div className={mergeClasses(styles.header, className)} {...rest}>{children}</div>
  );
};

export const CardContent: React.FC<DivProps> = ({ className, children, ...rest }) => {
  const styles = useSectionStyles();
  return (
    <div className={mergeClasses(styles.content, className)} {...rest}>{children}</div>
  );
};

export const CardTitle: React.FC<H3Props & { as?: 'h2' | 'h3' | 'h4' }> = ({ className, children, as: Heading = 'h3', ...rest }) => {
  const styles = useSectionStyles();
  return (
    <Heading className={mergeClasses(styles.title, className)} {...rest}>{children}</Heading>
  );
};

export const CardDescription: React.FC<PProps> = ({ className, children, ...rest }) => {
  const styles = useSectionStyles();
  return (
    <p className={mergeClasses(styles.description, className)} {...rest}>{children}</p>
  );
};


